import { Ellipsis } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import type { ShellNavigationItem, ShellNavigationSection, Workspace } from "../types/shell.js";
import {
  adminNavigation,
  employeeNavigation,
  mentorNavigationItem,
  supervisorNavigation,
} from "./shell-navigation.js";
import { fadeClass } from "./sidebar-state.js";

/** Collapsed rail shows this many destinations, then a More menu for the rest. */
export const collapsedItemLimit = 4;

export function navigationFor(workspace: Workspace, mentor = false): readonly ShellNavigationSection[] {
  if (workspace === "employee") return [{ labelKey: "nav.employeeSection", items: employeeNavigation }];
  if (workspace === "admin") return adminNavigation;
  return mentor
    ? [{ labelKey: "nav.mentorSection", items: [mentorNavigationItem] }, ...supervisorNavigation]
    : supervisorNavigation;
}

function isItemActive(pathname: string, item: ShellNavigationItem): boolean {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function RailTooltip({ label, group }: { label: string; group: "link" | "more" }) {
  return (
    <span
      className={`pointer-events-none absolute top-1/2 left-[calc(100%+0.625rem)] z-50 -translate-y-1/2 rounded-md bg-foreground px-2.5 py-1 text-xs font-medium whitespace-nowrap text-background opacity-0 shadow-sm transition-opacity ${
        group === "link"
          ? "group-hover/link:opacity-100 group-focus-visible/link:opacity-100"
          : "group-hover/more:opacity-100 group-focus-visible/more:opacity-100"
      }`}
      role="tooltip"
    >
      {label}
    </span>
  );
}

export function SidebarLink({
  expanded,
  item,
  labelsVisible,
}: {
  expanded: boolean;
  item: ShellNavigationItem;
  labelsVisible: boolean;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.labelKey);
  return (
    <NavLink
      aria-label={label}
      className={({ isActive }) =>
        `group/link relative flex shrink-0 rounded-xl transition-[height,background-color] duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-sidebar-foreground/60 ${
          expanded
            ? `h-11 flex-row items-start overflow-hidden pt-0.5 pl-2 ${isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "hover:bg-sidebar-foreground/10"}`
            : "h-16 w-full flex-col items-center justify-center gap-0.5"
        }`
      }
      data-control-id={item.controlId}
      end={item.end}
      to={item.to}
    >
      {({ isActive }) => (
        <>
          <span
            className={`relative grid size-10 shrink-0 place-items-center rounded-xl transition-colors ${
              expanded
                ? ""
                : isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "group-hover/link:bg-sidebar-foreground/10"
            }`}
          >
            <Icon aria-hidden="true" className="size-5" strokeWidth={1.7} />
            {expanded ? null : <RailTooltip group="link" label={label} />}
          </span>
          {expanded ? (
            <span
              aria-hidden="true"
              className={`ml-2 block h-10 min-w-0 flex-1 truncate pr-3 text-base leading-10 font-medium ${fadeClass(labelsVisible)}`}
            >
              {label}
            </span>
          ) : (
            <span
              aria-hidden="true"
              className={`max-w-full truncate px-1 text-center text-[10px] leading-3 font-medium ${fadeClass(labelsVisible)}`}
            >
              {label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function SidebarMoreMenu({
  items,
  labelsVisible,
  pathname,
}: {
  items: readonly ShellNavigationItem[];
  labelsVisible: boolean;
  pathname: string;
}) {
  const { t } = useTranslation();
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const label = t("nav.more");
  const active = items.some((item) => isItemActive(pathname, item));

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={root}>
      <button
        aria-controls={menuId}
        aria-current={active ? "page" : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        className="group/more relative flex h-16 w-full shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-sidebar-foreground/60"
        data-control-id="nav.more"
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <span
          className={`relative grid size-10 shrink-0 place-items-center rounded-xl transition-colors ${
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground"
              : open
                ? "bg-sidebar-foreground/10"
                : "group-hover/more:bg-sidebar-foreground/10"
          }`}
        >
          <Ellipsis aria-hidden="true" className="size-5" strokeWidth={1.7} />
          {open ? null : <RailTooltip group="more" label={label} />}
        </span>
        <span
          aria-hidden="true"
          className={`max-w-full truncate px-1 text-center text-[10px] leading-3 font-medium ${fadeClass(labelsVisible)}`}
        >
          {label}
        </span>
      </button>
      {open ? (
        <div
          className="absolute top-0 left-[calc(100%+1.5rem)] z-50 w-64 rounded-2xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg"
          data-control-id="nav.more.menu"
          id={menuId}
          role="menu"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                className={({ isActive }) =>
                  `flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 ${
                    isActive ? "bg-muted text-foreground" : "text-foreground hover:bg-muted"
                  }`
                }
                data-control-id={item.controlId}
                end={item.end}
                key={item.to}
                onClick={() => setOpen(false)}
                role="menuitem"
                to={item.to}
              >
                <Icon aria-hidden="true" className="size-5 shrink-0" strokeWidth={1.7} />
                {t(item.labelKey)}
              </NavLink>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
